import { useMemo, useState } from 'react';
import type { BookingRow } from '../types';
import { enqueueDeparture } from '../api/queue';
import { formatVenueTime } from '../lib/time';
import { bookingDisplayName } from '../lib/guest-name';

// Inline picker invoked from the Wachtrij section header. Lets the operator
// search arrived bookings not currently in the queue and add one. Mounts
// inline (not as a global modal) so it inherits the section's z-index
// stack and closes when clicking outside.

interface Props {
  /** Already filtered to arrived-but-not-queued by the caller. */
  arrivedNotInQueue: BookingRow[];
  onClose: () => void;
  /** Called after a successful enqueue. Parent should refresh the queue. */
  onAdded: () => void;
}

function nameOf(b: BookingRow): string {
  return bookingDisplayName(b);
}

export function QueueAddPicker({ arrivedNotInQueue, onClose, onAdded }: Props) {
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return arrivedNotInQueue;
    return arrivedNotInQueue.filter((b) => {
      const name = nameOf(b).toLowerCase();
      const num = (b.booking_number ?? '').toLowerCase();
      return name.includes(q) || num.includes(q);
    });
  }, [search, arrivedNotInQueue]);

  async function add(b: BookingRow) {
    if (busyId) return;
    setError(null);
    setBusyId(b.id);
    try {
      await enqueueDeparture(b.id);
      onAdded();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="queue-add-picker" role="dialog">
      <div className="queue-add-picker-header">
        <input
          type="text"
          className="queue-add-picker-search"
          autoFocus
          placeholder="Zoek op naam of boekingnummer…"
          value={search}
          onChange={(ev) => setSearch(ev.target.value)}
        />
        <button
          type="button"
          className="queue-add-picker-close"
          onClick={onClose}
          aria-label="Sluiten"
        >
          ✕
        </button>
      </div>

      {error ? (
        <div className="queue-add-picker-error" onClick={() => setError(null)}>
          {error}
        </div>
      ) : null}

      {arrivedNotInQueue.length === 0 ? (
        <div className="queue-add-picker-empty muted">
          Geen aangekomen boekingen buiten de wachtrij.
        </div>
      ) : filtered.length === 0 ? (
        <div className="queue-add-picker-empty muted">
          Geen resultaten voor &ldquo;{search}&rdquo;.
        </div>
      ) : (
        <ul className="queue-add-picker-list">
          {filtered.map((b) => {
            const slot = b.booking_slots[0];
            const name = nameOf(b) || '—';
            return (
              <li key={b.id}>
                <button
                  type="button"
                  className="queue-add-picker-row"
                  disabled={busyId !== null}
                  onClick={() => void add(b)}
                >
                  <span className="queue-add-picker-time">
                    {slot ? formatVenueTime(slot.slot_start) : '—'}
                  </span>
                  <span className="queue-add-picker-name">
                    <strong>{name}</strong>
                    <span className="muted"> · {b.party_size}p · {b.experiences?.name ?? '—'}</span>
                  </span>
                  <span className="queue-add-picker-num muted">{b.booking_number}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
